// The CMS console route subtree (content slice — Announcements). Code-based
// routes per DEV-GUIDE-002 §4 — a package-composed subtree hand-writes `.lazy()`
// + `createLazyRoute`. This factory hangs the `/cms` tree off the portal's root
// route so router.tsx stays the single wiring point (composed ALONGSIDE the LIB
// subtrees — neither touches the other).
//
// GATING is per-screen (router.tsx's documented choice): the CMS LAYOUT route
// admits any holder of a CMS capability (cms:announcements | cms:tenders | cms:events
// | cms:publications | cms:gallery | cms:media | cms:newsletter | cms:categories |
// cms:settings) via useSession/
// <Show>, and each TAB/tile re-checks its OWN cap for affordance visibility — the
// child SCREENS lean on the API's per-route rbac('cms:<type>') guard as the authority
// (the UI can only HIDE; the gateway is the authority,
// ADR-001 §14.2). Anonymous → /login; signed-in-without-any-CMS-grant → an explicit
// denied panel, never a blank Outlet.
import { lazyRoute } from "@erp/ui/route-kit";
import {
  BookOpen,
  Library,
  CalendarDays,
  Cookie,
  Image,
  Mail,
  Megaphone,
  Newspaper,
  Settings2,
  ShoppingCart,
  Tags,
} from "lucide-solid";
import { Outlet, createRoute } from "@tanstack/solid-router";
import { useSession } from "@erp/auth-kit/fe";
import { cmsQueries } from "../index.js";
import { CMS_CONSOLE_PERMS } from "@erp/module-cms/manifest";
import { AnnouncementStatus, AnnouncementType, CMS_CATEGORY_TYPES, CmsCategoryType, CookieCategory, EventStatus, GalleryAccessLevel, GalleryStatus, Locale, MediaKind, PublicationDocType, PublicationStatus, PublicationTitleKind, PublicationTitleRunStatus, SubscriberStatus, TenderStatus } from "../../schemas/index.js";
import { panelSearch } from "@erp/ui/side-panel";
import { ConsoleLayout } from "@erp/ui/console-layout";
import { PageHeading } from "@erp/ui/page-heading";
import { HubGrid } from "@erp/ui/hub-grid";
import { StatCard } from "@erp/ui/stat-card";
import { StatRowSkeleton } from "@erp/ui/skeleton";
import { useQuery } from "@tanstack/solid-query";
import { Show, createMemo } from "solid-js";
import { FilePenLine, Globe, Trash2 } from "lucide-solid";
import { MODULE_HUE } from "@erp/ui/category-hue";
import type { AnyRoute } from "@tanstack/solid-router";

/* THE CURSOR LEFT THE URL (2026-09-22, the infinite roll-out). Every list below
 * is an accumulating stream now, so there are no pages to name: a `?cursor=`
 * identified ONE page of twenty, and what a reader is looking at is a position
 * inside one growing list — a different amount of scrolling for every reader and
 * every window. Every other key (the facets, the language, the LIVE ⇄ TRASH
 * scope, the omnibox term) stays in the URL exactly as it was, so a shared or
 * bookmarked link still restores the view; what it no longer restores is how far
 * down the reader had got. A stale `?cursor=` on an old bookmark falls out of
 * these validators — TanStack still shows it in `location.search` when a parent
 * route has no validator of its own, but it reaches neither the stream's filters
 * nor its first page, so the list opens at the top. */

/* ─────────────────────── AnnouncementsList URL-as-state ───────────────────────
 * The keyset list's facet filters (?type/?status/?categoryId) live in
 * the URL (DEV-GUIDE-002 §4.1). Self-healing per the guide's ideal — the imported
 * Zod enums drive `.catch(undefined)` so a garbage `?type=`/`?status=` heals to
 * "no filter" rather than throwing (the whole point of validateSearch). No `z`
 * direct import: the schema objects carry their own `.optional().catch()`. */
export const ANNOUNCEMENTS_PAGE_SIZE = 20;

export interface AnnouncementsSearch {
  type?: AnnouncementType;
  status?: AnnouncementStatus;
  categoryId?: string;
  /** One locale's rows (exact-eq, e.g. "hi"); omitted (the default) lists every locale. */
  locale?: string;
  /** Opt-in TRASH view — "1" lists soft-deleted rows; omitted (the default) lists live. */
  trashed?: "1";
}

// Per-field self-healing parsers: a valid enum → itself; undefined/garbage →
// undefined (the ".catch()" contract the guide asks for), built off the SAME Zod
// enums the API validates with, so the UI and gateway can never disagree.
const typeParam = AnnouncementType.optional().catch(undefined);
const statusParam = AnnouncementStatus.optional().catch(undefined);

function optionalString(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

// ?trashed heals to the LIVE view unless it is the opt-in TRASH flag "1".
// URL-owned across every content list; the screen threads "1" → trashed:true into
// the list query (the API's ?trashed lists soft-deleted rows ALONE). Mirrors
// featuredFilter — a plain membership check, no `z` import. COERCED from the
// primitive (audit UX-17): the portal codec now hands "1" through as a string,
// and a codec that parsed it to the number 1 must read as the same flag rather
// than silently showing the live list — the validator, not the codec, owns shape.
function trashedFilter(v: unknown): "1" | undefined {
  return String(v) === "1" ? "1" : undefined;
}

// ?locale heals to "every locale" unless it is a well-formed tag (the ONE Locale grammar
// the API's ListQuery validates with — 2..10 chars). URL-owned across every content list
// and the categories manager; the screen threads it straight into the list query's
// exact-eq `locale` filter (0018). Absent is the default "All locales" view.
const localeParam = Locale.optional().catch(undefined);

export function validateAnnouncementsSearch(input: Record<string, unknown>): AnnouncementsSearch {
  return {
    type: typeParam.parse(input.type),
    status: statusParam.parse(input.status),
    categoryId: optionalString(input.categoryId),
    locale: localeParam.parse(input.locale),
    trashed: trashedFilter(input.trashed),
  };
}

/* ─────────────────────────── TendersList URL-as-state ───────────────────────────
 * The keyset register's ?status facet live in the URL (the AnnouncementsList
 * precedent). Self-healing: the imported TenderStatus enum drives `.catch(undefined)`
 * so a garbage `?status=` heals to "no filter" rather than throwing. */
export const TENDERS_PAGE_SIZE = 20;

export interface TendersSearch {
  status?: TenderStatus;
  /** Opt-in TRASH view — "1" lists soft-deleted rows; omitted (the default) lists live. */
  trashed?: "1";
}

const tenderStatusParam = TenderStatus.optional().catch(undefined);

export function validateTendersSearch(input: Record<string, unknown>): TendersSearch {
  return {
    status: tenderStatusParam.parse(input.status),
    trashed: trashedFilter(input.trashed),
  };
}

/* ─────────────────────────── EventsList URL-as-state ───────────────────────────
 * The keyset list's facet filters (?status/?featured) live in the URL
 * (the AnnouncementsList precedent). Self-healing: the imported EventStatus enum
 * drives `.catch(undefined)` so a garbage `?status=` heals to "no filter"; ?featured
 * is the stored INTEGER flag ("1" featured / "0" ordinary) and any other value heals
 * to "no filter" (a plain membership check — no `z` import, mirroring optionalString). */
export const EVENTS_PAGE_SIZE = 20;

export interface EventsSearch {
  status?: EventStatus;
  featured?: "0" | "1";
  /** One locale's rows (exact-eq, e.g. "hi"); omitted (the default) lists every locale. */
  locale?: string;
  /** Opt-in TRASH view — "1" lists soft-deleted rows; omitted (the default) lists live. */
  trashed?: "1";
}

const eventStatusParam = EventStatus.optional().catch(undefined);

// ?featured heals to "no filter" unless it is the stored "0"/"1" flag — coerced from
// the primitive, as trashedFilter is.
function featuredFilter(v: unknown): "0" | "1" | undefined {
  const flag = String(v);
  return flag === "0" || flag === "1" ? flag : undefined;
}

export function validateEventsSearch(input: Record<string, unknown>): EventsSearch {
  return {
    status: eventStatusParam.parse(input.status),
    featured: featuredFilter(input.featured),
    locale: localeParam.parse(input.locale),
    trashed: trashedFilter(input.trashed),
  };
}

/* ────────────────────────── PublicationsList URL-as-state ──────────────────────────
 * The document library's keyset list — its facet filters (?docType/?status/?categoryId)
 * live in the URL (the AnnouncementsList precedent). Self-healing: the imported
 * PublicationDocType / PublicationStatus enums drive `.catch(undefined)` so a garbage
 * `?docType=`/`?status=` heals to "no filter" rather than throwing. */
export const PUBLICATIONS_PAGE_SIZE = 20;

export interface PublicationsSearch {
  docType?: PublicationDocType;
  status?: PublicationStatus;
  categoryId?: string;
  /** One locale's rows (exact-eq, e.g. "hi"); omitted (the default) lists every locale. */
  locale?: string;
  /** Opt-in TRASH view — "1" lists soft-deleted rows; omitted (the default) lists live. */
  trashed?: "1";
}

const docTypeParam = PublicationDocType.optional().catch(undefined);
const publicationStatusParam = PublicationStatus.optional().catch(undefined);

export function validatePublicationsSearch(input: Record<string, unknown>): PublicationsSearch {
  return {
    docType: docTypeParam.parse(input.docType),
    status: publicationStatusParam.parse(input.status),
    categoryId: optionalString(input.categoryId),
    locale: localeParam.parse(input.locale),
    trashed: trashedFilter(input.trashed),
  };
}

/* ─────────────────────── PublicationTitlesList URL-as-state (0022) ───────────────────────
 * The titles register's keyset list — its facet filters (?kind / ?status / ?runStatus /
 * ?locale / ?q) live in the URL (the PublicationsList precedent). Self-healing: the
 * imported enums drive `.catch(undefined)` so a garbage `?kind=` heals to "no filter"; ?q is
 * the bounded name search the API already served the issue form's picker with (LIKE over
 * `name`), surfaced on the list by the ListView omnibox (2026-09-21) — a plain string, the
 * MediaLibrary ?search precedent. */
export const PUBLICATION_TITLES_PAGE_SIZE = 20;

export interface PublicationTitlesSearch {
  kind?: PublicationTitleKind;
  status?: PublicationStatus;
  runStatus?: PublicationTitleRunStatus;
  locale?: string;
  /** The omnibox's settled term — a LIKE over the title's name; omitted lists every title. */
  q?: string;
  trashed?: "1";
}

const titleKindParam = PublicationTitleKind.optional().catch(undefined);
const titleRunStatusParam = PublicationTitleRunStatus.optional().catch(undefined);

export function validatePublicationTitlesSearch(input: Record<string, unknown>): PublicationTitlesSearch {
  return {
    kind: titleKindParam.parse(input.kind),
    status: publicationStatusParam.parse(input.status),
    runStatus: titleRunStatusParam.parse(input.runStatus),
    locale: localeParam.parse(input.locale),
    q: optionalString(input.q),
    trashed: trashedFilter(input.trashed),
  };
}

/* ────────────────────────────── GalleryList URL-as-state ──────────────────────────────
 * The photo library's keyset list — its facet filters (?status/?collection/?accessLevel)
 * live in the URL (the AnnouncementsList precedent). Self-healing: the imported
 * GalleryStatus / GalleryAccessLevel enums drive `.catch(undefined)` so a garbage `?status=`
 * / `?accessLevel=` heals to "no filter"; ?collection is the free-text grouping NAME (an
 * exact-eq match — a plain membership check, mirroring optionalString). */
export const GALLERY_PAGE_SIZE = 20;

export interface GallerySearch {
  status?: GalleryStatus;
  collection?: string;
  accessLevel?: GalleryAccessLevel;
  /** One locale's rows (exact-eq, e.g. "hi"); omitted (the default) lists every locale. */
  locale?: string;
  /** Opt-in TRASH view — "1" lists soft-deleted rows; omitted (the default) lists live. */
  trashed?: "1";
}

const galleryStatusParam = GalleryStatus.optional().catch(undefined);
const galleryAccessLevelParam = GalleryAccessLevel.optional().catch(undefined);

export function validateGallerySearch(input: Record<string, unknown>): GallerySearch {
  return {
    status: galleryStatusParam.parse(input.status),
    collection: optionalString(input.collection),
    accessLevel: galleryAccessLevelParam.parse(input.accessLevel),
    locale: localeParam.parse(input.locale),
    trashed: trashedFilter(input.trashed),
  };
}

/* ─────────────────────────── MediaLibrary URL-as-state ───────────────────────────
 * The asset library's keyset grid — its facet filters (?kind/?search) live in
 * the URL (the AnnouncementsList precedent). Self-healing: the imported MediaKind enum
 * drives `.catch(undefined)` so a garbage `?kind=` heals to "no filter"; ?search is the
 * free-text filename query (a plain membership check, mirroring optionalString). */
export const MEDIA_PAGE_SIZE = 24;

export interface MediaSearch {
  kind?: MediaKind;
  search?: string;
}

const mediaKindParam = MediaKind.optional().catch(undefined);

export function validateMediaSearch(input: Record<string, unknown>): MediaSearch {
  return {
    kind: mediaKindParam.parse(input.kind),
    search: optionalString(input.search),
  };
}

/* ────────────────────────── CategoriesManager URL-as-state ──────────────────────────
 * The per-content-TYPE taxonomy manager owns the active ?type in the URL. Self-healing:
 * the imported CmsCategoryType enum drives `.catch(undefined)` so a garbage `?type=` heals
 * to "no selection" — the screen (and the loader) then default the effective type to the
 * first kind, so both re-key the SAME categoriesByType cache entry. No cursor: the taxonomy
 * is an UNPAGED bare array per type. */
export interface CategoriesSearch {
  type?: CmsCategoryType;
  /** One locale's rows (exact-eq, e.g. "hi"); omitted (the default) lists every locale. */
  locale?: string;
}

const categoryTypeParam = CmsCategoryType.optional().catch(undefined);

/** The taxonomy the manager opens on when `?type=` is absent — the first kind.
 *  ONE name for it: the loader's prefetch key, the screen's effective type and
 *  the filter spec's `defaultValue` all read this, so the three cannot drift. */
export const DEFAULT_CATEGORY_TYPE: CmsCategoryType = CMS_CATEGORY_TYPES[0];

export function validateCategoriesSearch(input: Record<string, unknown>): CategoriesSearch {
  return {
    type: categoryTypeParam.parse(input.type),
    locale: localeParam.parse(input.locale),
  };
}

/* ────────────────────────── CookiesManager URL-as-state ──────────────────────────
 * The consent registry manager owns the active ?category in the URL. Self-healing:
 * the imported CookieCategory enum drives `.catch(undefined)` so a garbage `?category=`
 * heals to "no filter" — the screen (and the loader) then list EVERY category. Unlike
 * CategoriesManager there is NO default category: an absent filter is the "all" view,
 * so the screen re-keys the SAME cookiesList({ category: undefined }) cache entry.
 * No cursor: the registry is an UNPAGED bare array (site config, not a keyset feed). */
export interface CookiesSearch {
  category?: CookieCategory;
}

const cookieCategoryParam = CookieCategory.optional().catch(undefined);

export function validateCookiesSearch(input: Record<string, unknown>): CookiesSearch {
  return {
    category: cookieCategoryParam.parse(input.category),
  };
}

/* ────────────────────────── NewsletterList URL-as-state ──────────────────────────
 * The subscriber list's keyset — its ?status facet, ?search (email/name — the API's
 * CmsSubscriberListQuery.search, surfaced on the list by the ListView omnibox since
 * 2026-09-21: an erase request arrives as an address, and paging for it was the only way
 * to find it) live in the URL (the AnnouncementsList precedent). Self-healing:
 * the imported SubscriberStatus enum drives `.catch(undefined)` so a garbage `?status=`
 * heals to "no filter" rather than throwing. No LIVE/TRASH toggle — a subscriber delete
 * is a HARD erase (no soft-delete column). */
export const NEWSLETTER_PAGE_SIZE = 20;

export interface NewsletterSearch {
  status?: SubscriberStatus;
  /** The omnibox's settled term — a LIKE over email and name; omitted lists everyone. */
  search?: string;
}

const subscriberStatusParam = SubscriberStatus.optional().catch(undefined);

export function validateNewsletterSearch(input: Record<string, unknown>): NewsletterSearch {
  return {
    status: subscriberStatusParam.parse(input.status),
    search: optionalString(input.search),
  };
}

/* ───────────────────── panel URL-state (the side-panel contract) ─────────────
 * A FORM or a VIEWER is a VIEW, so it lives in the URL: `?panel=<name>&panelId=
 * <recordId>&panelWide=1`. `panelSearch` heals the trio against a CLOSED set of
 * names per route — a `?panel=banana` (or a stale id with no panel) drops to "no
 * panel" instead of throwing, exactly like the facet/cursor validators above.
 * ABSENT panelId ⇒ create mode. The sets are exported so the screens and their
 * tests name the same strings.
 *
 * CategoriesManager / CookiesManager: the flat registries whose create/edit form
 * used to live in a LOCAL-state aside — the same two modes, now addressable.
 * The four rich content EDIT routes: the revision VIEWER (`?panel=revision&
 * panelId=<revNo>`), so a reviewer can link a colleague straight at one snapshot.
 * The MediaPicker stays a modal on purpose — it is a nested picker opened from
 * inside another form, and routing it would collide with the parent's panel URL. */
export const CMS_CATEGORY_PANELS = ["new", "edit"] as const;
export const CMS_COOKIE_PANELS = ["new", "edit"] as const;
export const CMS_REVISION_PANELS = ["revision"] as const;

/**
 * The CMS shell: admits any content editor, then renders the active child.
 *
 * The console opens for ANY ONE of the content capabilities (CMS_CONSOLE_PERMS,
 * owned by modules/cms's manifest so the menu and the gate can't drift apart) —
 * an editor holding only cms:events still gets in, and the per-screen gates
 * narrow from there. The tab strip that used to hide individual tabs per
 * capability is retired: the shell sidebar filters the same way, from the same
 * manifest, for every console.
 */
function CmsLayout() {
  return (
    <ConsoleLayout
      perm={CMS_CONSOLE_PERMS}
      deniedMessage="This account has no content-management access. Ask an administrator for it."
      skeletonId="cms-skeleton"
      deniedId="cms-denied"
      unreachableId="cms-unreachable"
    >
      <Outlet />
    </ConsoleLayout>
  );
}

/** `/cms` index — the landing with a card into each content register the role can reach. */
/** The sum of one figure over the sections the reader may count (absent ⇒ not counted). */
const sum = (rows: { total: number; published: number; draft: number; archived: number; trashed: number }[], key: "total" | "published" | "draft" | "trashed") =>
  rows.reduce((n, r) => n + r[key], 0);

/** A section's own strip for its hub card — live, published, drafts. */
const figuresFor = (
  rows: readonly { section: string; total: number; published: number; draft: number }[],
  section: string,
) => {
  const r = rows.find((x) => x.section === section);
  return r ? [
    { label: "Live", value: r.total.toLocaleString() },
    { label: "Published", value: r.published.toLocaleString() },
    { label: "Drafts", value: r.draft.toLocaleString() },
  ] : undefined;
};

/* Exported for the fe lane (test/console-home.test.tsx), beside the
   validate*Search helpers this file already exports for the same reason: the
   landing is an index-route component, so there is no lazy `Route` export to
   mount it by. */
export function CmsLanding() {
  const auth = useSession();
  /* ONE REQUEST for every card on this page (GET /stats). The six per-type
     `*Stats` queries exist for the registers themselves; firing all six here
     would put the arithmetic in the browser and let the tiles settle a render
     apart from each other. */
  const stats = useQuery(() => cmsQueries.consoleStats());
  const rows = createMemo(() => stats.data?.sections ?? []);
  return (
    <div>
      {/* Module identity capsule — MODULE_HUE.cms (DEV-GUIDE-002 §12.5) rides in
          PageHeading's own slot; the heading text stays the only accessible name. */}
      <PageHeading
        icon={Newspaper}
        hue={MODULE_HUE.cms}
        title="Content"
        subtitle="What the public website shows — announcements, events, publications, tenders, pages and media"
        help={{
          title: "What the website shows",
          children: (
            <p>
              Everything the public site publishes starts here. Each card opens one register — announcements,
              tenders, events, publications, the gallery and the media the pages use — where an item is
              written, reviewed and published; the site shows only what is published. The cards below the
              registers hold the site's reference lists and settings. Only the registers this account can edit
              are listed.
            </p>
          ),
        }}
      />
      {/* THE CONSOLE'S FIGURES (2026-10-05, the owner: the home "should have
          stats just like libraries … available per sub module"). The home named
          eleven registers and said nothing about what was in any of them, while
          every register below it carried a stat row of its own.

          GET /stats answers all six content sections in one request, already
          filtered to what this session may read — so the totals below are over
          the sections on screen, never over rows the reader cannot open.

          The tones are ./screens/content-stats.tsx's, by what each figure IS:
          a count of things wears the module's identity hue, Published is the
          healthy figure, Drafts is neither good nor bad, and In trash is a
          status that must be seen — filled while there is one, a quiet outline
          once the trash is empty. */}
      <Show when={rows().length > 0} fallback={<Show when={stats.isPending}><StatRowSkeleton id="cms-console-stats-loading" /></Show>}>
        <>
          <div id="cms-console-stats" class="mb-5 grid grid-cols-2 gap-4 xl:grid-cols-4">
            <StatCard
              id="stat-cms-total"
              label="Live items"
              value={sum(rows(), "total").toLocaleString()}
              icon={<Newspaper size={20} />}
              tone={MODULE_HUE.cms}
              hint="Every announcement, tender, event, publication and photo the console holds"
            />
            <StatCard
              id="stat-cms-published"
              label="Published"
              value={sum(rows(), "published").toLocaleString()}
              icon={<Globe size={20} />}
              tone="success"
              hint="What the public website can show right now"
            />
            <StatCard
              id="stat-cms-drafts"
              label="Drafts"
              value={sum(rows(), "draft").toLocaleString()}
              icon={<FilePenLine size={20} />}
              tone="neutral"
              hint="Written but not yet published"
            />
            <StatCard
              id="stat-cms-trash"
              label="In trash"
              value={sum(rows(), "trashed").toLocaleString()}
              icon={<Trash2 size={20} />}
              tone="warning"
              filled={sum(rows(), "trashed") > 0}
              hint="Removed from the site; restorable until purged"
            />
          </div>
        </>
      </Show>
      {/* Was eleven hand-written cards — each a Link wrapping a Card, a
          CardHeader, a padded body and a green `StatusBadge label="Open"` where
          a button belongs. @erp/ui/hub-grid is that shape, once; the per-card
          permission survives as `can`, filtered here rather than eleven
          <Show>s. */}
      <HubGrid
        id="cms-landing"
        hue={MODULE_HUE.cms}
        columns={4}
        items={[
    { id: "cms-nav-announcements", to: "/cms/announcements", label: "Announcements", blurb: "Notices, press releases and admissions", icon: Megaphone, can: auth.can("cms:announcements"), figures: figuresFor(rows(), "announcements") },
    { id: "cms-nav-tenders", to: "/cms/tenders", label: "Tenders", blurb: "Simple notices and complex procurements", icon: ShoppingCart, can: auth.can("cms:tenders"), figures: figuresFor(rows(), "tenders") },
    { id: "cms-nav-events", to: "/cms/events", label: "Events", blurb: "Calendar entries — dates, venue, tickets", icon: CalendarDays, can: auth.can("cms:events"), figures: figuresFor(rows(), "events") },
    { id: "cms-nav-publications", to: "/cms/publications", label: "Publications", blurb: "Prospectus, books, syllabi and documents", icon: BookOpen, can: auth.can("cms:publications"), figures: figuresFor(rows(), "publications") },
    { id: "cms-nav-publication-titles", to: "/cms/publication-titles", label: "Publication titles", blurb: "The journals, magazines and series the publications belong to", icon: Library, can: auth.can("cms:publications"), figures: figuresFor(rows(), "publicationTitles") },
    { id: "cms-nav-gallery", to: "/cms/gallery", label: "Gallery", blurb: "The photo library — catalogued images with rights", icon: Image, can: auth.can("cms:gallery"), figures: figuresFor(rows(), "gallery") },
    { id: "cms-nav-media", to: "/cms/media", label: "Media", blurb: "The images and documents the site uses", icon: Image, can: auth.can("cms:media") },
    { id: "cms-nav-newsletter", to: "/cms/newsletter", label: "Newsletter", blurb: "Subscribers captured from the public signup form", icon: Mail, can: auth.can("cms:newsletter") },
    { id: "cms-nav-categories", to: "/cms/categories", label: "Categories", blurb: "Per-content-type taxonomy — colours and order", icon: Tags, can: auth.can("cms:categories") },
    { id: "cms-nav-cookies", to: "/cms/cookies", label: "Cookies", blurb: "The consent registry the public banner describes", icon: Cookie, can: auth.can("cms:settings") },
    { id: "cms-nav-settings", to: "/cms/settings", label: "Settings", blurb: "Branding, publishing defaults and integrations", icon: Settings2, can: auth.can("cms:settings") },
        ].filter((l) => l.can)}
      />
    </div>
  );
}

/**
 * Build the `/cms` subtree under a portal's root route. Returns the routes to
 * spread into `rootRoute.addChildren([...])` (router.tsx). Screens close their own
 * `.lazy()` loop with `createLazyRoute('/cms/<x>')`. The list, `new`, and
 * `$announcementId/edit` are SIBLINGS under the shell (the catalogue pattern — the
 * form is never wrapped by the list route).
 */
export function makeCmsRoutes<TParent extends AnyRoute>(rootRoute: TParent) {
  const cmsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "cms",
    component: CmsLayout,
  });

  const indexRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "/",
    component: CmsLanding,
  });

  // The keyset list as one accumulating STREAM — the URL owns the filters; the loader WARMS the
  // SAME cache entry the screen's useQuery re-keys (§4.3 — the filters object here
  // MUST equal the screen's so both hit one cache entry).
  //
  // WARM, NOT AWAITED (audit UX-01, every loader below). A loader that returns
  // `ensureQueryData(...)` holds the route until the API answers: on a full load
  // the outlet is EMPTY for the whole wait (no pendingComponent is set, so the
  // router's fallback is nothing), and on a sidebar click the OLD screen stays
  // while the new one's rows load — measured at +2993 ms heading=data on
  // /cms/announcements behind a 3 s API delay. `void prefetchQuery(...)` starts
  // the same fetch (same key, same staleTime, deduped against the screen's own
  // read, still fired by `defaultPreload: "intent"` on hover) and returns at
  // once, so the route commits and the screen paints its heading and its own
  // bones from `queryState(...).pending()`. prefetchQuery never throws: a
  // failure lands in the cache entry, where the screen's ErrorCard reads it.
  //
  // `prefetchInfiniteQuery` warms the FIRST page only (its `pages` default is 1)
  // into the same accumulating entry the screen then reads, so the rule above is
  // unchanged and the reader's scroll fetches page two, never the loader.
  const announcementsRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "announcements",
    validateSearch: validateAnnouncementsSearch, // self-healing (§4.1)
    loaderDeps: ({ search }) => ({
      type: search.type,
      status: search.status,
      categoryId: search.categoryId,
      locale: search.locale,
      trashed: search.trashed,
    }),
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchInfiniteQuery(
        cmsQueries.announcementsListInfinite({
          limit: ANNOUNCEMENTS_PAGE_SIZE,
          type: deps.type,
          status: deps.status,
          categoryId: deps.categoryId,
          locale: deps.locale,
          trashed: deps.trashed === "1" ? true : undefined,
        }),
      );
    },
  }).lazy(lazyRoute(() => import("../screens/AnnouncementsList.js")));

  // Static `announcements/new` is registered before the dynamic
  // `announcements/$announcementId/edit` (path ranking makes static win
  // regardless; the order documents intent).
  const announcementNewRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "announcements/new",
  }).lazy(lazyRoute(() => import("../screens/AnnouncementForm.js")));

  const announcementEditRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "announcements/$announcementId/edit",
    // The revision VIEWER is a URL panel (`?panel=revision&panelId=<revNo>`).
    validateSearch: (input: Record<string, unknown>) => panelSearch(CMS_REVISION_PANELS, input),
  }).lazy(lazyRoute(() => import("../screens/AnnouncementForm.js"), (m) => m.EditRoute));

  // ── Tenders (the richest content type; the announcements sibling shape). The list
  //    streams over ?status; new + $tenderId/edit are SIBLINGS under
  //    the shell (the form is never wrapped by the list route). The loader warms the
  //    SAME cache entry the screen's useQuery re-keys (§4.3 — the filters object here
  //    MUST equal the screen's). The API re-checks rbac('cms:tenders') on every route. ──
  const tendersRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "tenders",
    validateSearch: validateTendersSearch, // self-healing (§4.1)
    loaderDeps: ({ search }) => ({
      status: search.status,
      trashed: search.trashed,
    }),
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchInfiniteQuery(
        cmsQueries.tendersListInfinite({
          limit: TENDERS_PAGE_SIZE,
          status: deps.status,
          trashed: deps.trashed === "1" ? true : undefined,
        }),
      );
    },
  }).lazy(lazyRoute(() => import("../screens/TendersList.js")));

  const tenderNewRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "tenders/new",
  }).lazy(lazyRoute(() => import("../screens/TenderForm.js")));

  const tenderEditRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "tenders/$tenderId/edit",
    // The revision VIEWER is a URL panel (`?panel=revision&panelId=<revNo>`).
    validateSearch: (input: Record<string, unknown>) => panelSearch(CMS_REVISION_PANELS, input),
  }).lazy(lazyRoute(() => import("../screens/TenderForm.js"), (m) => m.EditRoute));

  // ── Events (a calendar entry — the announcements sibling shape). The list keyset-
  //    streams over ?status + ?featured; new + $eventId/edit are SIBLINGS under
  //    the shell (the form is never wrapped by the list route). The loader warms the
  //    SAME cache entry the screen's useQuery re-keys (§4.3 — the filters object here
  //    MUST equal the screen's). The API re-checks rbac('cms:events') on every route. ──
  const eventsRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "events",
    validateSearch: validateEventsSearch, // self-healing (§4.1)
    loaderDeps: ({ search }) => ({
      status: search.status,
      featured: search.featured,
      locale: search.locale,
      trashed: search.trashed,
    }),
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchInfiniteQuery(
        cmsQueries.eventsListInfinite({
          limit: EVENTS_PAGE_SIZE,
          status: deps.status,
          featured: deps.featured,
          locale: deps.locale,
          trashed: deps.trashed === "1" ? true : undefined,
        }),
      );
    },
  }).lazy(lazyRoute(() => import("../screens/EventsList.js")));

  const eventNewRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "events/new",
  }).lazy(lazyRoute(() => import("../screens/EventForm.js")));

  const eventEditRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "events/$eventId/edit",
    // The revision VIEWER is a URL panel (`?panel=revision&panelId=<revNo>`).
    validateSearch: (input: Record<string, unknown>) => panelSearch(CMS_REVISION_PANELS, input),
  }).lazy(lazyRoute(() => import("../screens/EventForm.js"), (m) => m.EditRoute));

  // ── Publications (the document library — the announcements sibling shape). The list
  //    streams over ?docType + ?status + ?categoryId; new + $publicationId/
  //    edit are SIBLINGS under the shell (the form is never wrapped by the list route).
  //    The loader warms the SAME cache entry the screen's useQuery re-keys (§4.3 — the
  //    filters object here MUST equal the screen's). The API re-checks rbac('cms:publications')
  //    on every route. ──
  const publicationsRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "publications",
    validateSearch: validatePublicationsSearch, // self-healing (§4.1)
    loaderDeps: ({ search }) => ({
      docType: search.docType,
      status: search.status,
      categoryId: search.categoryId,
      locale: search.locale,
      trashed: search.trashed,
    }),
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchInfiniteQuery(
        cmsQueries.publicationsListInfinite({
          limit: PUBLICATIONS_PAGE_SIZE,
          docType: deps.docType,
          status: deps.status,
          categoryId: deps.categoryId,
          locale: deps.locale,
          trashed: deps.trashed === "1" ? true : undefined,
        }),
      );
    },
  }).lazy(lazyRoute(() => import("../screens/PublicationsList.js")));

  const publicationNewRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "publications/new",
  }).lazy(lazyRoute(() => import("../screens/PublicationForm.js")));

  const publicationEditRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "publications/$publicationId/edit",
    // The revision VIEWER is a URL panel (`?panel=revision&panelId=<revNo>`).
    validateSearch: (input: Record<string, unknown>) => panelSearch(CMS_REVISION_PANELS, input),
  }).lazy(lazyRoute(() => import("../screens/PublicationForm.js"), (m) => m.EditRoute));

  // ── Publication titles (0022 — the periodical / series a publication belongs to; the
  //    publications sibling shape). The list keyset-pages over ?kind + ?status + ?runStatus
  //    + ?locale; new + $titleId/edit are SIBLINGS under the shell. The API
  //    re-checks rbac('cms:publications') on every route. ──
  const publicationTitlesRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "publication-titles",
    validateSearch: validatePublicationTitlesSearch, // self-healing (§4.1)
    loaderDeps: ({ search }) => ({
      kind: search.kind,
      status: search.status,
      runStatus: search.runStatus,
      locale: search.locale,
      q: search.q,
      trashed: search.trashed,
    }),
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchInfiniteQuery(
        cmsQueries.publicationTitlesListInfinite({
          limit: PUBLICATION_TITLES_PAGE_SIZE,
          kind: deps.kind,
          status: deps.status,
          runStatus: deps.runStatus,
          locale: deps.locale,
          q: deps.q,
          trashed: deps.trashed === "1" ? true : undefined,
        }),
      );
    },
  }).lazy(lazyRoute(() => import("../screens/PublicationTitlesList.js")));

  const publicationTitleNewRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "publication-titles/new",
  }).lazy(lazyRoute(() => import("../screens/PublicationTitleForm.js")));

  const publicationTitleEditRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "publication-titles/$titleId/edit",
    validateSearch: (input: Record<string, unknown>) => panelSearch(CMS_REVISION_PANELS, input),
  }).lazy(lazyRoute(() => import("../screens/PublicationTitleForm.js"), (m) => m.EditRoute));

  // ── Gallery (the photo library, the LAST content type — the announcements sibling
  //    shape). The list streams over ?status + ?collection + ?accessLevel;
  //    new + $galleryId/edit are SIBLINGS under the shell (the form is never wrapped by
  //    the list route). The loader warms the SAME cache entry the screen's useQuery
  //    re-keys (§4.3 — the filters object here MUST equal the screen's). The API re-checks
  //    rbac('cms:gallery') on every route. A gallery item is NOT a companion-announcement
  //    target (a photo does not post to the notice feed). ──
  const galleryRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "gallery",
    validateSearch: validateGallerySearch, // self-healing (§4.1)
    loaderDeps: ({ search }) => ({
      status: search.status,
      collection: search.collection,
      accessLevel: search.accessLevel,
      locale: search.locale,
      trashed: search.trashed,
    }),
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchInfiniteQuery(
        cmsQueries.galleryItemsListInfinite({
          limit: GALLERY_PAGE_SIZE,
          status: deps.status,
          collection: deps.collection,
          accessLevel: deps.accessLevel,
          locale: deps.locale,
          trashed: deps.trashed === "1" ? true : undefined,
        }),
      );
    },
  }).lazy(lazyRoute(() => import("../screens/GalleryList.js")));

  const galleryNewRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "gallery/new",
  }).lazy(lazyRoute(() => import("../screens/GalleryForm.js")));

  const galleryEditRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "gallery/$galleryId/edit",
  }).lazy(lazyRoute(() => import("../screens/GalleryForm.js"), (m) => m.EditRoute));

  // ── Media (the R2 asset library — one route, no new/edit siblings: a "create" is
  //    the presign upload FLOW on the screen, not a form page). The list keyset-pages
  //    over ?kind + ?search; the loader warms the SAME cache entry the
  //    screen's useQuery re-keys (§4.3 — the filters object here MUST equal the
  //    screen's). The API re-checks rbac('cms:media') on every route (the per-row serve
  //    is additionally gated for private assets). ──
  const mediaRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "media",
    validateSearch: validateMediaSearch, // self-healing (§4.1)
    loaderDeps: ({ search }) => ({
      kind: search.kind,
      search: search.search,
    }),
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchInfiniteQuery(
        cmsQueries.mediaListInfinite({
          limit: MEDIA_PAGE_SIZE,
          kind: deps.kind,
          search: deps.search,
        }),
      );
    },
  }).lazy(lazyRoute(() => import("../screens/MediaLibrary.js")));

  // ── Newsletter (the subscriber list — one route, no new/edit siblings: subscribers
  //    arrive via the website's PUBLIC signup form, and a delete is a HARD erase, not a
  //    form page). The list streams over ?status; the loader warms the
  //    SAME cache entry the screen's useQuery re-keys (§4.3 — the filters object here
  //    MUST equal the screen's). The API re-checks rbac('cms:newsletter') on every route. ──
  const newsletterRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "newsletter",
    validateSearch: validateNewsletterSearch, // self-healing (§4.1)
    loaderDeps: ({ search }) => ({
      status: search.status,
      search: search.search,
    }),
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchInfiniteQuery(
        cmsQueries.subscribersListInfinite({
          limit: NEWSLETTER_PAGE_SIZE,
          status: deps.status,
          search: deps.search,
        }),
      );
    },
  }).lazy(lazyRoute(() => import("../screens/NewsletterList.js")));

  // ── Categories (the per-content-TYPE taxonomy manager — one route, no new/edit
  //    siblings: a flat registry edits inline in a right-side panel). The URL owns
  //    the active ?type; the loader warms the SAME categoriesByType cache entry the
  //    screen re-keys (default effective type = the first kind, matching the screen).
  //    The API re-checks rbac('cms:categories') on every route. ──
  const categoriesRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "categories",
    // ?type (§4.1) + the create/edit panel — both self-healing, one validator.
    validateSearch: (input: Record<string, unknown>) => ({
      ...validateCategoriesSearch(input),
      ...panelSearch(CMS_CATEGORY_PANELS, input),
    }),
    loaderDeps: ({ search }) => ({ type: search.type, locale: search.locale }),
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchQuery(cmsQueries.categoriesList({ type: deps.type ?? DEFAULT_CATEGORY_TYPE, locale: deps.locale }));
    },
  }).lazy(lazyRoute(() => import("../screens/CategoriesManager.js")));

  // ── Cookies (the consent registry — one route, no new/edit siblings: a flat site-
  //    config registry edits inline in a right-side panel, the CategoriesManager pattern).
  //    The URL owns the active ?category; the loader warms the SAME cookiesList cache
  //    entry the screen re-keys ({ category } — absent ⇒ the "all" view). Gated cms:settings
  //    (cookies are site config); the API re-checks rbac('cms:settings') on every route. ──
  const cookiesRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "cookies",
    // ?category (§4.1) + the create/edit panel — both self-healing, one validator.
    validateSearch: (input: Record<string, unknown>) => ({
      ...validateCookiesSearch(input),
      ...panelSearch(CMS_COOKIE_PANELS, input),
    }),
    loaderDeps: ({ search }) => ({ category: search.category }),
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchQuery(cmsQueries.cookiesList({ category: deps.category }));
    },
  }).lazy(lazyRoute(() => import("../screens/CookiesManager.js")));

  // ── Settings (site-wide CMS config — a single key/value editor, no filters). The
  //    loader warms the FULL merged configuration the screen hydrates from once. The
  //    API re-checks rbac('cms:settings') on the route. ──
  const settingsRoute = createRoute({
    getParentRoute: () => cmsRoute,
    path: "settings",
    loader: ({ context }) => {
      void context.queryClient.prefetchQuery(cmsQueries.settings());
    },
  }).lazy(lazyRoute(() => import("../screens/CmsSettings.js")));

  return [
    cmsRoute.addChildren([
      indexRoute,
      announcementsRoute,
      announcementNewRoute,
      announcementEditRoute,
      tendersRoute,
      tenderNewRoute,
      tenderEditRoute,
      eventsRoute,
      eventNewRoute,
      eventEditRoute,
      publicationsRoute,
      publicationNewRoute,
      publicationEditRoute,
      publicationTitlesRoute,
      publicationTitleNewRoute,
      publicationTitleEditRoute,
      galleryRoute,
      galleryNewRoute,
      galleryEditRoute,
      mediaRoute,
      newsletterRoute,
      categoriesRoute,
      cookiesRoute,
      settingsRoute,
    ]),
  ];
}
